import { z } from 'zod';
/**
 * Files attached to a record as proof.
 *
 * A receipt for an expense, a signed copy of an invoice, a bank screenshot
 * evidencing a payment. Accounting is an evidence trade: the figure is the
 * claim and the attachment is what supports it, and a book with no supporting
 * documents is one nobody can defend in an assessment.
 */
/**
 * What a file may be attached to. An ALLOW-LIST, and the reason is the same one
 * written down in docs/coding-standards.md for `doc_type`: a deny-list silently
 * admits whatever gets added next. A new attachable record has to be a
 * deliberate line here, not an accident of omission.
 *
 * `proforma_invoice` is included because a proforma and an invoice are the same
 * `documents` row with a different type, so excluding it would be arbitrary -
 * a customer's signed acceptance of a quote is exactly the kind of thing worth
 * keeping.
 *
 * `export_declaration` is a month's EDF (0032): the signed form and the
 * bank's acknowledgement are the proof that it was filed, and that date is
 * what FEMA runs on.
 *
 * Deliberately NOT here: customers and vendors. Identity documents are a
 * different feature with different retention rules, and quietly allowing them
 * through a receipts pipeline would be the wrong way to acquire it.
 */
export const ATTACHABLE_TYPES = ['expense', 'invoice', 'proforma_invoice', 'payment', 'export_declaration'];
export const attachableTypeSchema = z.enum(ATTACHABLE_TYPES);
export const attachTargetSchema = z
    .object({
    ownerType: attachableTypeSchema,
    ownerId: z.string().trim().min(1).max(36),
})
    .strict();
/**
 * What a file IS to its record (`files.role`, migration 0018).
 *
 * `source` is the original document the record was made FROM - the old
 * system's invoice PDF an imported invoice was recreated from, the supplier's
 * bill an expense was keyed off. It is distinct from a receipt attached
 * afterwards as proof, and the screen labels it "Source". Null - the default,
 * and every attachment before 0018 - is an ordinary attachment. An allow-list,
 * like `ATTACHABLE_TYPES`: a second role is a deliberate line here.
 */
export const FILE_ROLES = ['source'];
/** A record attachment's upload query: the target, and optionally what the file is to it. */
export const attachRecordUploadQuerySchema = attachTargetSchema
    .extend({
    role: z.enum(FILE_ROLES).optional(),
})
    .strict();
// ── The book's own images: logo and signature ─────────────────────────────
/**
 * What a file can be to the BOOK, as opposed to a record in it.
 *
 * `logo` prints top-left of every PDF; `signature` prints above the
 * "Authorized Signature" line. They go through the same upload route and the
 * same storage as a receipt - one pipeline, one size cap, one sniffer - but
 * they are not attachments: `ATTACHABLE_TYPES` is deliberately not widened,
 * so nothing that lists, deletes or cascades attachments ever sees a logo,
 * and the MCP attach tool cannot replace one. The role is REQUIRED here: a
 * file on the book with no stated purpose is a file nobody can find again.
 */
export const ORG_FILE_ROLES = ['logo', 'signature'];
/** Every `files.owner_type` a row may carry: the attachable records, and the book. */
export const FILE_OWNER_TYPES = [...ATTACHABLE_TYPES, 'org'];
/**
 * 2 MB, separate from `MAX_UPLOAD_MB`.
 *
 * A receipt photograph is legitimately 10 MB; a logo is not. It is embedded
 * in every PDF the book ever produces, so its weight is paid on every
 * invoice, and a 2 MB cap still admits a 2000-pixel PNG with room to spare.
 */
export const LETTERHEAD_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
/**
 * The formats a logo or signature may arrive in - and, after resizing, the
 * two it is stored as. PNG keeps a transparent background, which is what a
 * logo on white paper needs and what the receipt pipeline's everything-to-JPEG
 * rule would destroy. The PDF engine embeds exactly these two, which is why
 * WebP and HEIC are refused here rather than stored and then unprintable.
 */
export const LETTERHEAD_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
export const orgImageUploadQuerySchema = z
    .object({
    ownerType: z.literal('org'),
    /** The book's own id. Checked against the request's org, never trusted alone. */
    ownerId: z.string().trim().min(1).max(36),
    role: z.enum(ORG_FILE_ROLES),
})
    .strict();
/**
 * The multipart upload's query: a record target, or the book itself.
 *
 * A discriminated union rather than one loosened object, so `role=logo` on an
 * expense and `role=source` on the book are both refused as the shape errors
 * they are - a single object with every role optional would accept either
 * and store something no screen will ever show.
 */
export const attachUploadQuerySchema = z.discriminatedUnion('ownerType', [
    attachRecordUploadQuerySchema,
    orgImageUploadQuerySchema,
]);
/**
 * Uploading through MCP, where there is no multipart request to put a file in.
 *
 * The bytes arrive base64-encoded in a JSON body. That is unavoidable - a model
 * has no other way to hand over an image - and it is why the JSON body limit
 * has to be raised from Nest's 100 KB default.
 *
 * `mimeType` is accepted and then ignored for anything that matters: the server
 * sniffs the real type from the bytes. It is taken at all only so an error
 * message can say what the caller *thought* it was sending.
 */
export const attachFileSchema = attachTargetSchema
    .extend({
    filename: z.string().trim().min(1, 'The file needs a name').max(255),
    mimeType: z.string().trim().max(120).optional(),
    contentBase64: z.string().min(1, 'The file is empty'),
    role: z.enum(FILE_ROLES).optional(),
})
    .strict();
export const listAttachmentsQuerySchema = attachTargetSchema;
/**
 * Base64 inflates by 4/3, plus padding and any whitespace a client inserts.
 *
 * Used to size the JSON body limit from `MAX_UPLOAD_MB` rather than picking a
 * number and hoping. 1.4 leaves room for line-wrapped base64, which some
 * clients still produce.
 */
export const BASE64_OVERHEAD = 1.4;
/** How long a signed download URL stays valid. */
export const DOWNLOAD_URL_TTL_SECONDS = 300;
/**
 * Human-readable bytes. Lives here rather than in the web app because the API
 * puts sizes in error messages ("that file is 14.2 MB; the limit is 10 MB") and
 * the two must not disagree about what a megabyte is.
 */
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024)
        return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}
//# sourceMappingURL=attachments.js.map